import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import CourseEnrollmentsPanel from '../../components/admin/CourseEnrollmentsPanel';
import StudentProfileCard from '../../components/admin/StudentProfileCard';
import { userService, enrollmentService } from '../../api';
import {
    Mail,
    FileText, ArrowLeft,
    ExternalLink, Edit, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import AppSelect from '../../components/ui/AppSelect'

function StudentDetail() {
    const { id } = useParams();
    const [student, setStudent] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        user_status: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [userRes, docsRes, enrRes] = await Promise.all([
                userService.getStudentDetail(id),
                userService.getSupportingDocuments(id),
                enrollmentService.getEnrollments({ user: id })
            ]);

            setStudent(userRes.data);
            setDocuments(docsRes.data.results || docsRes.data);
            setEnrollments(enrRes.data.results || enrRes.data);
        } catch (err) {
            toast.error('Failed to load student details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleVerifyDoc = async (docId) => {
        try {
            setActionLoading(docId);
            await userService.verifyDocument(docId);
            toast.success('Document verified');
            fetchData();
        } catch (err) {
            toast.error('Failed to verify document');
        } finally {
            setActionLoading(null);
        }
    };

    const handleUnverifyDoc = async (docId) => {
        try {
            setActionLoading(docId);
            await userService.unverifyDocument(docId);
            toast.success('Document unverified');
            fetchData();
        } catch (err) {
            toast.error('Failed to unverify document');
        } finally {
            setActionLoading(null);
        }
    };

    const handleApprove = async () => {
        try {
            setActionLoading('status');
            await userService.approveStudent(id);
            toast.success('Student approved');
            fetchData();
        } catch (err) {
            toast.error('Failed to approve student');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        const reason = window.prompt('Enter reason for rejection:');
        if (reason === null) return; // Cancelled

        try {
            setActionLoading('status');
            await userService.rejectStudent(id, reason);
            toast.success('Student rejected');
            fetchData();
        } catch (err) {
            toast.error('Failed to reject student');
        } finally {
            setActionLoading(null);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            setActionLoading('edit');
            await userService.updateStudent(id, editFormData);
            toast.success('Student updated successfully');
            setIsEditModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error('Failed to update student');
        } finally {
            setActionLoading(null);
        }
    };

    const openEditModal = () => {
        setEditFormData({
            first_name: student.first_name || '',
            last_name: student.last_name || '',
            phone_number: student.phone_number || student.phone || '',
            user_status: student.user_status || student.status || 'pending'
        });
        setIsEditModalOpen(true);
    };

    if (loading) return (
        <DashboardLayout>
            <div className="flex items-center justify-center p-2xl">Loading student details...</div>
        </DashboardLayout>
    );

    if (!student) return null;
    const fullName = `${student.first_name || ""}${" "}${student.last_name || ""}`.trim() || student.username;

    const resolvedStatus = student.user_status || student.status || 'pending';

    return (
        <DashboardLayout>
            <main className="container" style={{ padding: 'var(--spacing-xl)', paddingTop: '100px' }}>
                <div className="flex items-center justify-between mb-lg">
                    <Link to="/admin/students" className="flex items-center gap-sm text-muted" style={{ textDecoration: 'none' }}>
                        <ArrowLeft size={16} /> Back to Students
                    </Link>
                    
                    <button 
                        onClick={openEditModal}
                        className="btn btn-primary flex items-center gap-sm hover:opacity-90 transition-all"
                        style={{ 
                            padding: '0.6rem 1.25rem', 
                            borderRadius: '14px', 
                            fontWeight: 600, 
                            color: 'white',
                            cursor: 'pointer',
                            zIndex: 10
                        }}
                    >
                        <Edit size={18} /> Edit Student
                    </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--spacing-xl)', alignItems: 'start' }}>

                    {/* Left Column: Stats & Actions */}
                    <div className="flex flex-col gap-xl">
                        <StudentProfileCard
                            student={student}
                            fullName={fullName}
                            status={resolvedStatus}
                            actionLoading={actionLoading}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            onEdit={openEditModal}
                        />

                        {/* Recent Activity Placeholder */}
                        <div style={{ padding: 'var(--spacing-lg)', backgroundColor: 'var(--color-card-bg)', borderRadius: 20, border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)' }}>
                            <h4 style={{ marginBottom: '1rem' }}>Learning Stats</h4>
                            <div className="flex flex-col gap-sm">
                                <div className="flex justify-between items-center bg-theme-bg p-sm rounded-lg">
                                    <span className="text-muted font-medium">Courses</span>
                                    <span className="font-bold">{enrollments.length}</span>
                                </div>
                                <div className="flex justify-between items-center bg-theme-bg p-sm rounded-lg">
                                    <span className="text-muted font-medium">Completed</span>
                                    <span className="font-bold">{enrollments.filter(e => e.status === 'completed').length}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Documents & Enrollments */}
                    <div className="flex flex-col gap-xl">

                        {/* Supporting Documents */}
                        <div style={{ backgroundColor: 'var(--color-card-bg)', borderRadius: 24, border: '1px solid var(--color-border)', overflow: 'hidden',
                  color: 'var(--color-text-primary)' }}>
                            <div style={{ padding: '1.2rem', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between' }}>
                                <h3 className="flex items-center gap-sm"><FileText size={20} /> Supporting Documents</h3>
                            </div>
                            <div style={{ padding: 'var(--spacing-lg)' }}>
                                {documents.length === 0 ? (
                                    <p className="text-muted text-center py-lg">No documents uploaded.</p>
                                ) : (
                                    <div className="flex flex-col gap-md">
                                        {documents.map(doc => (
                                            <div key={doc.id} className="flex items-center justify-between p-md border rounded-xl">
                                                <div className="flex items-center gap-md">
                                                    <div style={{ color: 'var(--color-primary)' }}><FileText /></div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{doc.document_type || 'Document'}</div>
                                                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                            {doc.is_verified ? '✅ Verified' : '⏳ Pending verification'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-md">
                                                    <a href={doc.file} target="_blank" rel="noreferrer" className="btn btn-outline" style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', textDecoration: 'none', color: 'inherit' }}>
                                                        <ExternalLink size={14} /> View
                                                    </a>
                                                    {doc.is_verified ? (
                                                        <button
                                                            onClick={() => handleUnverifyDoc(doc.id)}
                                                            disabled={actionLoading === doc.id}
                                                            className="btn text-danger"
                                                            style={{ fontSize: '0.85rem' }}
                                                        >
                                                            Unverify
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleVerifyDoc(doc.id)}
                                                            disabled={actionLoading === doc.id}
                                                            className="btn text-success"
                                                            style={{ fontSize: '0.85rem' }}
                                                        >
                                                            Verify
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <CourseEnrollmentsPanel student={student} enrollments={enrollments} />

                    </div>
                </div>
            </main>

            {/* Edit Student Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-theme-card rounded-[40px] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-theme-border flex flex-col">
                        
                        {/* Modal Header */}
                        <div className="px-10 py-8 border-b border-theme-border flex justify-between items-center bg-theme-bg/30">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-primary/10 rounded-2xl text-primary">
                                    <Edit size={28} />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-extrabold text-theme-text tracking-tight">Edit Student Information</h3>
                                    <p className="text-sm text-theme-muted font-medium">Update profile details and account status</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsEditModalOpen(false)} 
                                className="p-2 hover:bg-theme-bg rounded-full transition-all text-theme-muted hover:text-theme-text"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        
                        {/* Modal Body */}
                        <form onSubmit={handleEditSubmit} className="p-10 space-y-10">
                            {/* Row 1: Names */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3">
                                    <label className="block text-[10px] font-bold text-theme-muted uppercase tracking-[0.2em] ml-1">First Name</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-5 py-4 rounded-2xl border border-theme-border bg-theme-bg text-theme-text placeholder:text-theme-muted/50 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                                        value={editFormData.first_name}
                                        onChange={e => setEditFormData({ ...editFormData, first_name: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="block text-[10px] font-bold text-theme-muted uppercase tracking-[0.2em] ml-1">Last Name</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-5 py-4 rounded-2xl border border-theme-border bg-theme-bg text-theme-text placeholder:text-theme-muted/50 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                                        value={editFormData.last_name}
                                        onChange={e => setEditFormData({ ...editFormData, last_name: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* Row 2: Contact Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3">
                                    <label className="block text-[10px] font-bold text-theme-muted uppercase tracking-[0.2em] ml-1">Phone Number</label>
                                    <input
                                        type="text"
                                        className="w-full px-5 py-4 rounded-2xl border border-theme-border bg-theme-bg text-theme-text placeholder:text-theme-muted/50 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                                        value={editFormData.phone_number}
                                        onChange={e => setEditFormData({ ...editFormData, phone_number: e.target.value })}
                                        placeholder="Not provided"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="block text-[10px] font-bold text-theme-muted uppercase tracking-[0.2em] ml-1">Email (Account ID)</label>
                                    <div className="w-full px-5 py-4 rounded-2xl border border-dashed border-theme-border bg-theme-bg/50 text-theme-text font-semibold flex items-center gap-3 opacity-70 cursor-not-allowed">
                                        <Mail size={18} className="text-theme-muted" />
                                        {student.email}
                                    </div>
                                    <p className="text-[10px] text-theme-muted font-bold italic ml-1">Note: Email addresses are unique identifiers and cannot be changed.</p>
                                </div>
                            </div>

                            {/* Row 3: Status */}
                            <div className="space-y-3 max-w-md">
                                <label className="block text-[10px] font-bold text-theme-muted uppercase tracking-[0.2em] ml-1">Membership Status</label>
                                <div className="relative">
                                    <AppSelect
                                        className="w-full px-5 py-4 rounded-2xl border border-theme-border bg-theme-bg text-theme-text appearance-none focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-bold"
                                        value={editFormData.user_status}
                                        onChange={e => setEditFormData({ ...editFormData, user_status: e.target.value })}
                                    >
                                        <option value="pending">Pending Review</option>
                                        <option value="approved">Approved & Active</option>
                                        <option value="rejected">Rejected / Denied</option>
                                        <option value="inactive">Inactive / Suspended</option>
                                    </AppSelect>
                                    <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-theme-muted">
                                        <Clock size={18} />
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="flex justify-end items-center gap-4 pt-4 border-t border-theme-border mt-4">
                                <button 
                                    type="button" 
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="px-8 py-4 rounded-2xl font-bold text-theme-text hover:bg-theme-bg border border-transparent hover:border-theme-border transition-all"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={actionLoading === 'edit'}
                                    className="px-10 py-4 rounded-2xl font-extrabold bg-primary text-white shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3"
                                >
                                    {actionLoading === 'edit' ? (
                                        <Clock className="animate-spin" size={20} />
                                    ) : (
                                        <CheckCircle size={20} />
                                    )}
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}

export default StudentDetail;
